import "./buttons.scss";
import { type FC, type HTMLProps, type MouseEvent } from "react";
import { type DialogOptions } from "../../Actions/Dialogs/DialogActions";

export interface ButtonProps extends Omit<HTMLProps<HTMLButtonElement>, "ref" | "className">
{
    classes?: Array<string>;
    padding?: string;
    focus?: boolean;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
    noColours?: boolean;
}

export const CancelOptionButton: DialogOptions = {
    label  : "Cancel",
    classes: [ "button-cancel" ],
};

export const OKOptionButton: DialogOptions = {
    label  : "OK",
    classes: [ "button-cancel" ],
};

export const NoOptionButton: DialogOptions = {
    label  : "No",
    classes: [ "button-cancel" ],
};

export const DISABLED_BUTTON_CLASSES = "disabled:outline-hidden disabled:text-gray-500 disabled:bg-gray-300 " +
    "hover:disabled:text-gray-500 hover:disabled:bg-gray-300 disabled:cursor-not-allowed";

export const DEFAULT_BUTTON_CLASSES = "border border-current cursor-pointer " +
    "select-none text-[1em] transition-colors";

export const DEFAULT_PADDING_CLASSES = "px-2 py-1";

export const DEFAULT_BUTTON_COLOUR_CLASSES = "bg-gray-200";

const Button: FC<ButtonProps> = ({
    classes,
    disabled,
    type,
    children,
    noColours,
    padding,
    ...otherProps
}) => {
    const classArray = [ "button", DEFAULT_BUTTON_CLASSES, DISABLED_BUTTON_CLASSES ];

    if(!noColours) {
        classArray.push(DEFAULT_BUTTON_COLOUR_CLASSES);
    }

    if(typeof padding !== "string") {
        classArray.push(DEFAULT_PADDING_CLASSES);
    } else if(padding) {
        classArray.push(padding);
    }

    if(Array.isArray(classes)) {
        classArray.push(...classes);
    }

    const onClick = (e: MouseEvent<HTMLButtonElement>) => {
        if(disabled) {
            e.preventDefault();
            e.stopPropagation();
        } else if(typeof otherProps.onClick === "function") {
            return otherProps.onClick(e);
        }
    };

    const onMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
        if(disabled) {
            e.preventDefault();
            e.stopPropagation();
        } else if(typeof otherProps.onMouseDown === "function") {
            otherProps.onMouseDown(e);
        }
    };

    const onMouseLeave = (e: MouseEvent<HTMLButtonElement>) => {
        if(disabled) {
            e.preventDefault();
            e.stopPropagation();
        } else if(typeof otherProps.onMouseLeave === "function") {
            otherProps.onMouseLeave(e);
        }
    };

    return (
        <button
            {...otherProps}
            type={type || "button"}
            disabled={disabled}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            className={classArray.join(" ")}
        >
            {children}
        </button>
    );
};

export default Button;
